// Sports Nutrition Engine — i18n (EN/RO toggle)
import React, { createContext, useContext, useState, useEffect } from "react";

const dict = {
  en: {
    nav: { signin: "Sign In", pricing: "Pricing", logout: "Log Out" },
    landing: {
      eyebrow: "PERFORMANCE NUTRITION SYSTEM",
      h1_a: "Built for athletes who",
      h1_b: "respect themselves.",
      sub: "TDEE, macros, weekly meal plans, supplements and vitamins — calibrated to your sport, position, training load, and the way you sweat.",
      cta_start: "Start Now",
      cta_have_code: "I have an access code",
      cta_demo: "Try a Demo Plan",
      f1_title: "Precision TDEE",
      f1_body: "Energy expenditure, macro split and recovery loads computed from sport science research — not generic calculators. Recalibrates on every check-in.",
      f2_title: "Sport-Specific Engine",
      f2_body: "Every sport. Every position. A defender does not eat like a striker; a rower does not eat like a sprinter. The engine knows the difference and tunes everything accordingly.",
      f3_title: "Real-World Adaptive Plans",
      f3_body: "Plans that fit travel days, match days, recovery days and double sessions. Symptom-driven adjustments. Realistic food swaps. Built to live with — not against — your week.",
      humanitarian_badge: "HUMANITARIAN BONUS",
      humanitarian_title: "Healthy Living for the athlete's family",
      humanitarian_body: "Your access code also unlocks a Healthy Living mode — designed so the athlete's family can eat healthy alongside them. Same code, same quota, just toggle the profile type at onboarding.",
      video_eyebrow: "// 03 — HOW IT WORKS",
      video_title: "Video Tutorials",
      video_sub: "Quick walkthroughs to get the most out of the engine.",
      video_soon: "Coming Soon",
      video_1_title: "Build Your First Plan",
      video_1_desc: "Profile, sport, position, goal — and you get a complete daily breakdown in under 60 seconds.",
      video_2_title: "Daily Check-in: Real Adaptations",
      video_2_desc: "How sweat level, sleep and muscle soreness change tomorrow's plan automatically.",
      video_3_title: "Cravings without Guilt",
      video_3_desc: "Use the cravings tab right — keep the macros on target while enjoying real food.",
      newsletter_eyebrow: "// 03 — FREE GUIDE",
      newsletter_title: "Get the free Athlete Nutrition Quick Guide",
      newsletter_sub: "8-10 pages, no fluff: TDEE, EPA/DHA, hydration, top 5 supplements, young-athlete safety.",
      newsletter_cta: "Download the PDF",
      // Real-athletes section
      real_eyebrow: "// FOR REAL ATHLETES",
      real_title: "Sports Nutrition Engine",
      real_intro_p1: "Sports Nutrition Engine is built for athletes in the real world — not just the ones from ideal tables and clean scenarios.",
      real_intro_p2: "We know performance isn't built in a perfect world. There's school, work, travel, exams, fatigue, temptations, unplanned emotional events, missed meals and days when the plan isn't followed all the way. That's exactly why we built a platform that tracks both performance and steady progress.",
      real_intro_p3: "We picked practical solutions for the moments when life can't stay constant for the athlete — or the athlete can't stay constant for the plan. Because long-term success isn't only made of a few perfect days; it's also the ability to get back on track when things slip.",
      real_feat_1_title: "Personalized Plans",
      real_feat_1_body: "Adapted to your sport, position on the field, age, weight, height and goal.",
      real_feat_2_title: "Ziua Mea (My Day)",
      real_feat_2_body: "A system that shows you in real time where you stand and what you can still do to close the day well — even when the plan isn't fully followed.",
      real_feat_3_title: "Cravings",
      real_feat_3_body: "Because athletes are human too. Performance also means the ability to smartly weave small food pleasures into your lifestyle and sports goals.",
      real_feat_4_title: "Check-In",
      real_feat_4_body: "For tracking energy, recovery, sleep and overall state — so the plan stays connected to the athlete's reality.",
      diff_title: "What sets us apart?",
      diff_q: "Sports Nutrition Engine was built around one simple question:",
      diff_q_emph: "How can we help athletes follow a plan in the real world?",
      diff_l1: "We track progress.",
      diff_l2: "We build tools for the real athlete.",
      diff_l3: "We support performance through consistency, adaptation and better day-to-day decisions.",
      diff_closing_a: "Because performance isn't the result of one perfect day.",
      diff_closing_b: "It's the result of hundreds of good-enough days.",
      pricing_title: "Choose Your Access",
      pricing_sub: "Pilot program",
      pkg_4mo: "4 Months · 10 plans", pkg_1y: "1 Year · 25 plans",
      pkg_buy: "Request Code",
      pkg_best: "BEST VALUE",
    },
    pricing: {
      title: "Pricing",
      desc: "Pay once. Receive your access code by email. Use on any device.",
      access_label: "Access duration",
      buy: "Continue to checkout",
      email_label: "Email for access code",
      email_placeholder: "you@example.com",
      validity_days: "days",
    },
    login: {
      title: "Sign in",
      subtitle: "Enter your access code",
      code_label: "Access code",
      code_placeholder: "XXXX-XXXX-XXXX",
      submit: "Enter",
      no_code: "No code? Purchase one",
      invalid: "Invalid or expired access code",
    },
    onboarding: {
      title: "Build Your Profile",
      step: "Step",
      next: "Next", back: "Back", finish: "Generate Plan",
      sex: "Sex", sex_m: "Male", sex_f: "Female",
      profile_type: "Profile Type",
      profile_pro: "Professional Athlete",
      profile_lifestyle: "Healthy Living (Family bonus)",
      profile_lifestyle_hint: "Humanitarian bonus — share your athlete's plan with family members. Same access code, same quota.",
      pro_lock_warning: "This app is individual. Name, age, sport and position cannot be changed until your access code expires. Make sure they're correct.",
      name: "Name", name_placeholder: "Athlete name (used inside the app)",
      name_placeholder_lifestyle: "Name (used inside the app)",
      err_name: "Please enter a name (at least 2 characters).",
      confirm_step1: "Confirm identity — these fields stay locked for the entire duration of your code (incl. after 'New plan'):\n\nName: {name}\nAge: {age}\nSex: {sex}\n\nDouble-check before proceeding.",
      confirm_step2: "Confirm sport & position — stay locked for the entire duration of your code (incl. after 'New plan'):\n\nSport: {sport}\nPosition: {position}\n\nDouble-check before proceeding.",
      age: "Age (years)", weight: "Weight (kg)", height: "Height (cm)",
      sport: "Sport", position: "Position",
      activity_level: "Training Volume",
      activity_level_lifestyle: "Daily Activity Type",
      activity: { sedentary: "Sedentary", light: "Light", moderate: "Moderate", intense: "Intense", performance: "Performance (5-6/week)", elite: "Elite (2/day)" },
      activity_lifestyle: { sedentary: "Sedentary", light: "Light", moderate: "Moderate", intense: "Active", performance: "Very Active" },
      goal: "Goal",
      goals: { maintain: "Maintenance", muscle: "Build Muscle", cut: "Cut Weight", performance: "Performance", recovery: "Recovery" },
      goals_lifestyle: { maintain: "Maintenance", cut: "Weight Loss", muscle: "Gain Mass", recomposition: "Body Recomposition (lose fat + gain muscle)" },
      special: "Special Conditions",
      special_opts: { vegetarian: "Vegetarian", vegan: "Vegan", injury_recovery: "Injury Recovery" },
    },
    dashboard: {
      tabs: { overview: "Overview", daily: "Daily Plan", weekly: "Weekly", supplements: "Supplements", vitamins: "Vitamins", checkin: "Check-in", cravings: "Cravings", foodlog: "My Day" },
      tdee: "Daily TDEE", macros: "Macros", protein: "Protein", carbs: "Carbs", fat: "Fat",
      hydration: "Hydration", today: "TODAY",
      kcal: "kcal",
      junior_capped: "Junior safety cap applied (45 kcal/kg)",
      locked_identity: "LOCKED IDENTITY",
      download_pdf: "Download PDF Plan",
      edit_profile: "Edit Profile",
      plans_remaining: "Plans remaining",
      plans_unlimited: "Unlimited plans",
      new_plan: "Start New Plan",
      new_plan_confirm: "⚠ This will RESET your current plan AND consume another plan from your quota (e.g. 9/10 → 8/10).\n\nLocked identity fields (name, age, sex, sport, position) stay the same — only weight, height, goal and training volume can be changed. Continue?",
      no_plans_left: "No plan generations remaining on your code. Please upgrade.",
      demo_banner: "DEMO MODE — explore the engine. Get full access to save your own plan.",
      demo_cta: "Get full access →",
      seats_title: "Seats used",
      seats_athlete: "athlete",
      seats_athletes: "athletes",
      seats_family: "family",
      seats_of: "of",
    },
    plan: {
      meal_breakfast: "Breakfast",
      meal_snack1: "Mid-morning Snack",
      meal_lunch: "Lunch",
      meal_preworkout: "Pre/Post-Workout",
      meal_snack2: "Afternoon Snack",
      meal_dinner: "Dinner",
      swap: "Swaps",
      soup_swap: "Soup swap",
      total: "Total",
      meals_mode: "Meal Structure",
      meals_5: "Standard (recommended)",
      meals_3: "3 meals (travel / camp)",
      structure_label: "Structure:",
      main_meals: "main meals",
      snacks_word: "technical snacks",
      disc_title: "Methodological note",
      disc_body: "Sports Nutrition Engine prioritizes: (1) hitting daily energy needs (kcal); (2) covering carbohydrate needs for performance; (3) realistic, easy-to-implement portions; (4) keeping protein and fat targets within physiologically acceptable limits. To keep the plan practical and sustainable, some values may show moderate variation from theoretical targets. These variations are considered normal and do not significantly impact sports performance as long as overall energy intake and macronutrient distribution are respected. The goal of this plan is not mathematical perfection — it is consistent application under real-life and training conditions. The plan can be personalized at any time.",
      week_swap_note: "Note: Training, match and rest days can be swapped to fit your competition schedule or coach/athlete choice. The structure is indicative; what matters is the macros per day type, not the calendar day.",
      meals_tip_title: "Travel / camp tip",
      meals_tip_body: "When you cannot prepare your own meals (away games, camps, hotel buffet), it is OK to compress 4 meals into 3. Total daily calories and macros matter far more than meal count. The 3-meal mode below regroups the same food into Breakfast, Lunch and Dinner without changing your targets.",
      day_types: { hard_training: "Hard Training", light_training: "Light Training", rest: "Rest", match: "Match", double_session: "Double Session", standard: "Standard Day" },
      day_types_short: { hard_training: "Hard", light_training: "Light", rest: "Rest", match: "Match", double_session: "Double", standard: "Standard" },
      days: { monday: "Monday", tuesday: "Tuesday", wednesday: "Wednesday", thursday: "Thursday", friday: "Friday", saturday: "Saturday", sunday: "Sunday" },
    },
    checkin: {
      title: "Daily Check-in",
      energy: "Energy", sleep: "Sleep", stress: "Stress", soreness: "Muscle Soreness", sweat: "Sweat Level",
      sweat_help: "How heavy did you sweat today? (1 = barely, 10 = soaked)",
      symptoms: "Symptoms",
      cramps: "Cramps", dizziness: "Dizziness in effort", poor_digestion: "Poor digestion",
      weight_loss: "Unexpected weight loss",
      amenorrhea: "Amenorrhea (female athlete)",
      day_type: "Today's day type",
      submit: "Get Adaptations",
      status_green: "GREEN — performance ready",
      status_yellow: "YELLOW — minor adjustments",
      status_orange: "ORANGE — recovery focus",
      status_red: "RED — medical evaluation required",
      adaptations: "Adaptations",
      medical_alert: "MEDICAL ALERT",
    },
    cravings: {
      title: "Cravings",
      subtitle: "Max 1 craving per day. The engine rebuilds the day around it.",
      apply: "Apply to Today",
      day_key: "Day",
      day_type: "Day Type",
      meal: "Insert into meal",
      remove: "Clear",
      warning: "Warning",
      not_allowed: "Not allowed for this day type",
      max_week: "Max per week",
    },
    success: {
      title: "Payment Successful",
      desc: "Check your email — your access code is on the way.",
      check_status: "Checking payment status...",
      go_login: "Go to Sign In",
    },
    common: {
      loading: "Loading...",
      grams: "g",
      kcal: "kcal",
      back_home: "Back to home",
      copyright: "TriboiAI LLC · sportsnutritionengine.online",
    },
  },
  ro: {
    nav: { signin: "Conectare", pricing: "Prețuri", logout: "Deconectare" },
    landing: {
      eyebrow: "SISTEM DE NUTRIȚIE DE PERFORMANȚĂ",
      h1_a: "Pentru sportivii care",
      h1_b: "se respectă.",
      sub: "TDEE, macros, planuri săptămânale, suplimente și vitamine — calibrate pe sportul tău, post, volum de antrenament și transpirație.",
      cta_start: "Începe acum",
      cta_have_code: "Am cod de acces",
      cta_demo: "Vezi un Plan Demo",
      f1_title: "TDEE de Precizie",
      f1_body: "Consum energetic, structură macros și încărcare de recuperare calculate din literatura de știință sportivă — nu calculatoare generice. Se recalibrează la fiecare check-in.",
      f2_title: "Motor Specific Sportului",
      f2_body: "Fiecare sport. Fiecare post. Un fundaș nu mănâncă la fel ca un atacant; un canotor nu mănâncă la fel ca un sprinter. Motorul cunoaște diferența și ajustează totul în consecință.",
      f3_title: "Planuri Adaptive Reale",
      f3_body: "Planuri compatibile cu deplasări, zile de meci, zile de recuperare și sesiuni duble. Ajustări pe simptome. Variante reale de înlocuire. Făcute să funcționeze cu, nu împotriva, săptămânii tale.",
      humanitarian_badge: "BONUS UMANITAR",
      humanitarian_title: "Healthy Living pentru familia sportivului",
      humanitarian_body: "Codul tău de acces deblochează și un mod Healthy Living — gândit ca familia sportivului să mănânce sănătos alături de el. Același cod, aceeași cotă, doar selectezi tipul de profil la onboarding.",
      pricing_title: "Alege Accesul",
      pricing_sub: "Program pilot",
      pkg_4mo: "4 Luni · 10 planuri", pkg_1y: "1 An · 25 planuri",
      pkg_buy: "Cere Cod",
      pkg_best: "RECOMANDAT",
      video_eyebrow: "// 03 — CUM FUNCȚIONEAZĂ",
      video_title: "Tutoriale Video",
      video_sub: "Ghiduri rapide pentru a folosi motorul la maxim.",
      video_soon: "În curând",
      video_1_title: "Construiește primul tău plan",
      video_1_desc: "Profil, sport, post, obiectiv — și ai un plan zilnic complet în mai puțin de 60 de secunde.",
      video_2_title: "Check-in zilnic: Adaptări reale",
      video_2_desc: "Cum schimbă transpirația, somnul și febra musculară planul de mâine, automat.",
      video_3_title: "Pofte fără remușcări",
      video_3_desc: "Folosește tab-ul de pofte corect — păstrezi macros-urile pe țintă cu mâncare reală.",
      newsletter_eyebrow: "// 03 — GHID GRATUIT",
      newsletter_title: "Primește gratuit Ghidul Rapid de Nutriție Sportivă",
      newsletter_sub: "8-10 pagini, fără umplutură: TDEE, EPA/DHA, hidratare, top 5 suplimente, recomandări pentru sportivi tineri.",
      newsletter_cta: "Descarcă-mi PDF-ul",
      // Secțiunea Real-athletes
      real_eyebrow: "// PENTRU SPORTIVII REALI",
      real_title: "Sports Nutrition Engine",
      real_intro_p1: "Sports Nutrition Engine vine în ajutorul sportivilor din viața reală, nu doar din tabelele și scenariile ideale.",
      real_intro_p2: "Știm că performanța nu se construiește într-o lume perfectă. Există școală, serviciu, deplasări, examene, oboseală, tentații, evenimente psihoemoționale neplanificate, mese ratate și zile în care planul nu este respectat integral. Tocmai de aceea am construit o platformă care urmărește atât performanța, cât și progresul constant.",
      real_intro_p3: "Am ales soluții practice și pentru momentele în care viața nu poate fi constantă pentru sportiv sau sportivul nu poate fi constant pentru plan. Pentru că succesul pe termen lung nu este dat doar de câteva zile perfecte, ci și de capacitatea de a reveni pe direcția corectă atunci când apar abateri.",
      real_feat_1_title: "Planuri personalizate",
      real_feat_1_body: "Adaptate sportului practicat, poziției în teren, vârstei, greutății, înălțimii și obiectivului urmărit.",
      real_feat_2_title: "Ziua Mea",
      real_feat_2_body: "Un sistem care îți arată în timp real unde te afli și ce mai poți face pentru a închide ziua cât mai bine, chiar și atunci când planul nu a fost respectat integral.",
      real_feat_3_title: "Pofte",
      real_feat_3_body: "Pentru că sportivii sunt oameni. Performanța înseamnă și capacitatea de a integra inteligent micile plăceri alimentare în stilul de viață și în obiectivele sportive.",
      real_feat_4_title: "Check-In",
      real_feat_4_body: "Pentru monitorizarea energiei, recuperării, somnului și stării generale, astfel încât planul să rămână conectat la realitatea sportivului.",
      diff_title: "Ce ne diferențiază?",
      diff_q: "Sports Nutrition Engine a fost construit pornind de la o întrebare simplă:",
      diff_q_emph: "Cum îi putem ajuta pe sportivi să respecte un plan în lumea reală?",
      diff_l1: "Urmărim progresul.",
      diff_l2: "Construim instrumente pentru sportivul real.",
      diff_l3: "Susținem performanța prin consecvență, adaptare și decizii mai bune zi de zi.",
      diff_closing_a: "Pentru că performanța nu este rezultatul unei zile perfecte.",
      diff_closing_b: "Este rezultatul a sute de zile suficient de bune.",
    },
    pricing: {
      title: "Prețuri",
      desc: "Plătești o dată. Primești codul pe email. Folosești pe orice dispozitiv.",
      access_label: "Durata accesului",
      buy: "Continuă la plată",
      email_label: "Email pentru cod",
      email_placeholder: "tu@example.com",
      validity_days: "zile",
    },
    login: {
      title: "Conectare",
      subtitle: "Introdu codul tău de acces",
      code_label: "Cod de acces",
      code_placeholder: "XXXX-XXXX-XXXX",
      submit: "Intră",
      no_code: "Nu ai cod? Cumpără unul",
      invalid: "Cod de acces invalid sau expirat",
    },
    onboarding: {
      title: "Profilul Tău",
      step: "Pas",
      next: "Următor", back: "Înapoi", finish: "Generează Plan",
      sex: "Sex", sex_m: "Masculin", sex_f: "Feminin",
      profile_type: "Tip Profil",
      profile_pro: "Sportiv Profesionist",
      profile_lifestyle: "Healthy Living (Bonus familie)",
      profile_lifestyle_hint: "Bonus umanitar — împărtășește planul sportivului tău cu membrii familiei. Același cod de acces, aceeași cotă.",
      pro_lock_warning: "Această aplicație este individuală. Numele, vârsta, sportul și postul NU se mai pot schimba până la expirarea codului. Verifică să fie corecte.",
      name: "Nume", name_placeholder: "Numele sportivului (folosit în aplicație)",
      name_placeholder_lifestyle: "Nume (folosit în aplicație)",
      err_name: "Te rugăm să introduci un nume (minimum 2 caractere).",
      confirm_step1: "Confirmă identitatea — aceste câmpuri rămân blocate pe toată durata codului tău (la fel și după 'Plan nou'):\n\nNume: {name}\nVârstă: {age}\nSex: {sex}\n\nVerifică să fie corecte. Continui?",
      confirm_step2: "Confirmă sportul și postul — rămân blocate pe toată durata codului tău (la fel și după 'Plan nou'):\n\nSport: {sport}\nPost: {position}\n\nVerifică să fie corecte. Continui?",
      age: "Vârstă (ani)", weight: "Greutate (kg)", height: "Înălțime (cm)",
      sport: "Sport", position: "Post",
      activity_level: "Volum Antrenament",
      activity_level_lifestyle: "Tip activitate zilnică",
      activity: { sedentary: "Sedentar", light: "Ușor", moderate: "Moderat", intense: "Intens", performance: "Performanță (5-6/săpt)", elite: "Elită (2/zi)" },
      activity_lifestyle: { sedentary: "Sedentar (birou, fără mișcare)", light: "Activ ușor (mers, plimbări)", moderate: "Moderat (3-4 mișcări/săpt)", intense: "Activ (5+ mișcări/săpt)", performance: "Hiperactiv (zilnic intens)" },
      goal: "Obiectiv",
      goals: { maintain: "Menținere", muscle: "Masă Musculară", cut: "Pierdere Greutate", performance: "Performanță", recovery: "Recuperare" },
      goals_lifestyle: { maintain: "Menținere", cut: "Slăbire", muscle: "Masă (creștere)", recomposition: "Recompoziție corporală (slăbire grăsime + creștere musculară simultan)" },
      special: "Condiții Speciale",
      special_opts: { vegetarian: "Vegetarian", vegan: "Vegan", injury_recovery: "Recuperare Accidentare" },
    },
    dashboard: {
      tabs: { overview: "Sumar", daily: "Plan Zilnic", weekly: "Săptămânal", supplements: "Suplimente", vitamins: "Vitamine", checkin: "Check-in", cravings: "Pofte", foodlog: "Ziua mea" },
      tdee: "TDEE Zilnic", macros: "Macros", protein: "Proteină", carbs: "Carbohidrați", fat: "Grăsimi",
      hydration: "Hidratare", today: "AZI",
      kcal: "kcal",
      junior_capped: "Cap junior aplicat (45 kcal/kg)",
      locked_identity: "IDENTITATE FIXĂ",
      download_pdf: "Descarcă Plan PDF",
      edit_profile: "Editează Profil",
      plans_remaining: "Planuri rămase",
      plans_unlimited: "Planuri nelimitate",
      new_plan: "Plan Nou de la Zero",
      new_plan_confirm: "⚠ Asta va RESETA planul curent ȘI va consuma încă un plan din cota ta (ex: 9/10 → 8/10).\n\nCâmpurile de identitate (nume, vârstă, sex, sport, post) rămân aceleași — poți modifica doar greutate, înălțime, obiectiv și volum antrenament. Continui?",
      no_plans_left: "Nu mai ai planuri rămase pe acest cod. Te rugăm să cumperi un plan nou.",
      demo_banner: "MOD DEMO — testează motorul. Pentru a-ți salva propriul plan, obține acces complet.",
      demo_cta: "Acces complet →",
      seats_title: "Locuri folosite",
      seats_athlete: "sportiv",
      seats_athletes: "sportivi",
      seats_family: "familie",
      seats_of: "din",
    },
    plan: {
      meal_breakfast: "Mic Dejun",
      meal_snack1: "Gustare Dimineață",
      meal_lunch: "Prânz",
      meal_preworkout: "Pre/Post-Antrenament",
      meal_snack2: "Gustare după-amiază",
      meal_dinner: "Cină",
      swap: "Variante",
      soup_swap: "Schimbă cu ciorbă",
      total: "Total",
      meals_mode: "Structură mese",
      meals_5: "Standard (recomandat)",
      meals_3: "3 mese (deplasare / cantonament)",
      structure_label: "Structură:",
      main_meals: "mese principale",
      snacks_word: "gustări tehnice",
      // Disclaimer
      disc_title: "Notă metodologică",
      disc_body: 'Motorul Sports Nutrition Engine prioritizează: (1) atingerea necesarului energetic (calorii); (2) acoperirea necesarului de carbohidrați pentru performanță; (3) porții alimentare realiste și ușor de implementat; (4) respectarea țintelor de proteine și grăsimi în limite fiziologic acceptabile. Pentru a păstra planul practic și sustenabil, unele valori pot prezenta variații moderate față de țintele teoretice. Aceste variații sunt considerate normale și nu influențează semnificativ performanța sportivă atunci când aportul energetic și distribuția generală a macronutrienților sunt respectate. Scopul acestui plan nu este perfecțiunea matematică, ci aplicarea consecventă în condițiile reale de viață și antrenament. Planul poate fi personalizat în orice moment. Utilizatorul poate ajusta gramajele, înlocui alimentele cu alternativele disponibile și adapta mesele în funcție de preferințe, disponibilitate sau contextul zilnic, păstrând principiile generale ale planului.',
      week_swap_note: "Notă: Zilele cu tipurile de antrenament, meci sau odihnă pot fi interschimbate în funcție de programul competițional sau alegerea sportivului/antrenorului. Structura este orientativă; macros-urile per tip de zi sunt cele care contează, nu ziua calendaristică.",
      meals_tip_title: "Sfat pentru deplasare / cantonament",
      meals_tip_body: "Când nu îți poți pregăti singur mesele (meciuri în deplasare, cantonamente, hotel cu bufet), e perfect OK să comprimi cele 4 mese în 3. Totalul caloric și macros zilnice contează mult mai mult decât numărul de mese. Modul 3 mese de mai jos regrupează aceeași mâncare în Mic Dejun, Prânz și Cină, fără să-ți schimbe țintele.",
      day_types: { hard_training: "Antrenament Greu", light_training: "Antrenament Ușor", rest: "Odihnă", match: "Meci", double_session: "Sesiune Dublă", standard: "Zi Standard" },
      day_types_short: { hard_training: "Greu", light_training: "Ușor", rest: "Odihnă", match: "Meci", double_session: "Dublă", standard: "Standard" },
      days: { monday: "Luni", tuesday: "Marți", wednesday: "Miercuri", thursday: "Joi", friday: "Vineri", saturday: "Sâmbătă", sunday: "Duminică" },
    },
    checkin: {
      title: "Check-in Zilnic",
      energy: "Energie", sleep: "Somn", stress: "Stres", soreness: "Febră Musculară", sweat: "Nivel Transpirație",
      sweat_help: "Cât de mult ai transpirat azi? (1 = puțin, 10 = leoarcă)",
      symptoms: "Simptome",
      cramps: "Crampe", dizziness: "Amețeli în efort", poor_digestion: "Digestie slabă",
      weight_loss: "Pierdere greutate neașteptată",
      amenorrhea: "Amenoree (sportivă feminin)",
      day_type: "Tipul zilei",
      submit: "Adaptează",
      status_green: "VERDE — gata de performanță",
      status_yellow: "GALBEN — ajustări minore",
      status_orange: "PORTOCALIU — focus pe recuperare",
      status_red: "ROȘU — evaluare medicală necesară",
      adaptations: "Adaptări",
      medical_alert: "ALERTĂ MEDICALĂ",
    },
    cravings: {
      title: "Pofte",
      subtitle: "Maxim 1 poftă pe zi. Motorul reconstruiește ziua în jurul ei.",
      apply: "Aplică pe Azi",
      day_key: "Zi",
      day_type: "Tipul zilei",
      meal: "Inserează la masa",
      remove: "Șterge",
      warning: "Atenție",
      not_allowed: "Nepermis pentru acest tip de zi",
      max_week: "Max pe săptămână",
    },
    success: {
      title: "Plată Reușită",
      desc: "Verifică emailul — codul tău de acces e pe drum.",
      check_status: "Verific statusul plății...",
      go_login: "Mergi la Conectare",
    },
    common: {
      loading: "Se încarcă...",
      grams: "g",
      kcal: "kcal",
      back_home: "Înapoi acasă",
      copyright: "TriboiAI LLC · sportsnutritionengine.online",
    },
  },
};

const I18nContext = createContext({ lang: "en", setLang: () => {}, t: dict.en });

export const I18nProvider = ({ children }) => {
  const [lang, setLang] = useState(() => localStorage.getItem("sne_lang") || "en");
  useEffect(() => { localStorage.setItem("sne_lang", lang); }, [lang]);
  const t = dict[lang] || dict.en;
  return <I18nContext.Provider value={{ lang, setLang, t }}>{children}</I18nContext.Provider>;
};

export const useI18n = () => useContext(I18nContext);
